import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CourseCandidate } from '../../services/courseCandidatesService';
import type { CourseRun, CourseDocument } from '../../services/courseRunsService';
import { courseRunsService } from '../../services/courseRunsService';
import { jobsService, JobRecord } from '../../services/jobsService';

export interface CourseContextValue {
  candidate: CourseCandidate | null;
  run: CourseRun | null;
  document: CourseDocument | null;
  loading: boolean;
  error: string | null;
  refreshDocument: () => Promise<void>;
  refreshRun: () => Promise<void>;
  executeStage: (
    stageId: string,
    options?: {
      payload?: Record<string, unknown>;
      onUpdate?: (job: JobRecord) => void;
    }
  ) => Promise<JobRecord>;
  isExecuting: boolean;
  selectedStageId: string | null;
  setSelectedStageId: (id: string | null) => void;
  activeLessonRunId: string | null;
  setActiveLessonRunId: (id: string | null) => void;
}

const CourseContext = createContext<CourseContextValue | null>(null);

export function useCourseContext(): CourseContextValue {
  const ctx = useContext(CourseContext);
  if (!ctx) {
    throw new Error('useCourseContext must be used within a CourseContextProvider');
  }
  return ctx;
}

interface CourseContextProviderProps {
  candidate: CourseCandidate | null;
  run: CourseRun | null;
  children: React.ReactNode;
}

export function CourseContextProvider({
  candidate,
  run: initialRun,
  children,
}: CourseContextProviderProps) {
  const [run, setRun] = useState<CourseRun | null>(initialRun);
  const [document, setDocument] = useState<CourseDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>('course_setup');
  const [isExecuting, setIsExecuting] = useState(false);
  const [activeLessonRunId, setActiveLessonRunId] = useState<string | null>(null);

  // Sync run when a different course is selected
  const currentRunId = useRef(initialRun?.run_id);
  useEffect(() => {
    if (initialRun?.run_id !== currentRunId.current) {
      currentRunId.current = initialRun?.run_id;
      setRun(initialRun);
      setSelectedStageId('course_setup');
    }
  }, [initialRun?.run_id, initialRun]);

  const refreshRun = useCallback(async () => {
    const runId = run?.run_id;
    if (!runId) return;
    try {
      const fresh = await courseRunsService.get(runId);
      setRun(fresh);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to refresh run';
      console.error(msg);
    }
  }, [run?.run_id]);

  const refreshDocument = useCallback(async () => {
    const runId = run?.run_id;
    if (!runId) {
      setDocument(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const doc = await courseRunsService.getDocument(runId);
      setDocument(doc);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load document';
      setError(msg);
      setDocument(null);
    } finally {
      setLoading(false);
    }
  }, [run?.run_id]);

  const executeStage = useCallback(async (
    stageId: string,
    options?: { payload?: Record<string, unknown>; onUpdate?: (job: JobRecord) => void },
  ): Promise<JobRecord> => {
    const runId = run?.run_id;
    if (!runId) throw new Error('No run selected');

    setIsExecuting(true);
    try {
      const { job_id } = await courseRunsService.executeStage(runId, stageId);
      const finalJob = await jobsService.waitForCompletion(job_id, {
        onUpdate: options?.onUpdate,
      });
      await refreshRun();
      await refreshDocument();
      if (finalJob.status === 'failed') {
        throw new Error((finalJob as Record<string, unknown>).error as string || 'Stage execution failed');
      }
      return finalJob;
    } finally {
      setIsExecuting(false);
    }
  }, [run?.run_id, refreshRun, refreshDocument]);

  // Auto-load document when run changes
  useEffect(() => {
    if (run?.run_id) {
      void refreshDocument();
    }
  }, [run?.run_id, refreshDocument]);

  const value = useMemo<CourseContextValue>(
    () => ({
      candidate,
      run,
      document,
      loading,
      error,
      refreshDocument,
      refreshRun,
      executeStage,
      isExecuting,
      selectedStageId,
      setSelectedStageId,
      activeLessonRunId,
      setActiveLessonRunId,
    }),
    [candidate, run, document, loading, error, refreshDocument, refreshRun, executeStage, isExecuting, selectedStageId, activeLessonRunId],
  );

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
}
